import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../styles/home.css";

export const HomePage: React.FC = () => {
  const [trackingNum, setTrackingNum] = useState("");
  const navigate = useNavigate();

  const [installPromptEvent, setInstallPromptEvent] = useState<any>(null);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    const handler = (e: any) => {
      e.preventDefault();
      setInstallPromptEvent(e);
      setCanInstall(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstallClick = async () => {
    if (!installPromptEvent) return;
    installPromptEvent.prompt();
    const choice = await installPromptEvent.userChoice;
    if (choice?.outcome === "accepted") setCanInstall(false);
    else setCanInstall(false);
    setInstallPromptEvent(null);
  };

  const handleTrack = (e: React.FormEvent) => {
    e.preventDefault();
    if (trackingNum.trim()) navigate(`/track/${trackingNum.trim()}`);
  };

  const features = [
    {
      icon: "📦",
      bg: "#eff6ff",
      title: "Real-Time Tracking",
      desc: "Follow every shipment from pickup to delivery with live GPS updates and status notifications.",
    },
    {
      icon: "🏢",
      bg: "#f0fdf4",
      title: "Multi-Branch Operations",
      desc: "Manage multiple branches under one company account with centralised visibility and control.",
    },
    {
      icon: "🔐",
      bg: "#fdf4ff",
      title: "Role-Based Access",
      desc: "Purpose-built roles ensure every team member sees exactly what they need.",
    },
    {
      icon: "💳",
      bg: "#fff7ed",
      title: "Payment Management",
      desc: "Track payments per shipment, mark collections, handle refunds and flag failed transactions.",
    },
    {
      icon: "🔗",
      bg: "#f0f9ff",
      title: "Webhook Integrations",
      desc: "Push shipment events to your own systems automatically with HMAC-signed payloads and retry logic.",
    },
    {
      icon: "📊",
      bg: "#fefce8",
      title: "Reports & Audit Logs",
      desc: "Full audit trail on every action. Export reports by date, branch, status or payment type.",
    },
  ];

  const steps = [
    {
      n: "1",
      title: "Create Shipment",
      desc: "Fill in receiver details, select branch and add items. Sender info fills automatically.",
    },
    {
      n: "2",
      title: "Confirm & Dispatch",
      desc: "Admin confirms the order. Dispatch agent assigns a transporter and marks it departed.",
    },
    {
      n: "3",
      title: "Track in Transit",
      desc: "GPS coordinates and status updates flow in real time. Anyone with the tracking number can follow along.",
    },
    {
      n: "4",
      title: "Deliver & Collect",
      desc: "Agent receives at destination branch. Payment is recorded and the shipment is closed.",
    },
  ];

  return (
    <div className="hp">
      {/* ── Nav ── */}
      <nav className="hp-nav">
        <Link to="/home">
          <img src="/logo.png" alt="E.H.C Logistics" className="hp-nav-logo" />
        </Link>
        <div className="hp-nav-links">
          <a href="#features" className="hp-nav-link">Features</a>
          <a href="#how-it-works" className="hp-nav-link">How it works</a>
          <Link to="/track" className="hp-nav-link">Track</Link>
          <Link to="/logistics-rwanda" className="hp-nav-link">Resources</Link>
          <Link to="/login" className="hp-nav-cta">Sign in</Link>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="hp-hero">
        <div className="hp-hero-inner">
          <div>
            <div className="hp-hero-eyebrow">
              <span>🚚</span> E.H.C Logistics Platform
            </div>
            <h1 className="hp-hero-title">
              Rwanda's #1 Logistics
              <br />
              <span>& Courier Platform</span>
            </h1>
            <p className="hp-hero-sub">
              The most complete logistics management platform for Rwanda and
              East Africa. Real-time shipment tracking, multi-branch operations,
              inventory management, and payment processing — trusted by leading
              logistics companies in Kigali, Kampala, Nairobi, and Dar es
              Salaam.
            </p>
            <div className="hp-hero-actions">
              <Link to="/register" className="hp-btn-primary">
                Get started free →
              </Link>
              <Link to="/track" className="hp-btn-ghost">
                📦 Track a shipment
              </Link>
            </div>
          </div>

          <div className="hp-hero-visual">
            <div className="hp-track-card">
              <div className="hp-track-card-label">Quick Track</div>
              <form onSubmit={handleTrack}>
                <div className="hp-track-input-row">
                  <input
                    className="hp-track-input"
                    placeholder="Enter tracking number…"
                    value={trackingNum}
                    onChange={(e) => setTrackingNum(e.target.value)}
                  />
                  <button type="submit" className="hp-track-btn">
                    Track
                  </button>
                </div>
              </form>
              <div className="hp-status-pills">
                <span className="hp-pill hp-pill-blue">● In Transit</span>
                <span className="hp-pill hp-pill-green">● Delivered</span>
                <span className="hp-pill hp-pill-amber">● Pending</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="hp-section" id="features">
        <div className="hp-section-inner">
          <div className="hp-section-tag">Platform Features</div>
          <h2 className="hp-section-title">Everything your team needs</h2>
          <p className="hp-section-sub">
            From first-mile pickup to last-mile delivery, every step is tracked,
            recorded and accessible to the right people.
          </p>
          <div className="hp-features-grid">
            {features.map((f) => (
              <div className="hp-feature-card" key={f.title}>
                <div className="hp-feature-icon" style={{ background: f.bg }}>
                  {f.icon}
                </div>
                <div className="hp-feature-title">{f.title}</div>
                <div className="hp-feature-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Coverage ── */}
      <section className="hp-section hp-coverage">
        <div className="hp-section-inner">
          <div className="hp-section-tag">East Africa Coverage</div>
          <h2 className="hp-section-title">
            Serving logistics companies across Rwanda & East Africa
          </h2>
          <p className="hp-section-sub">
            E.H.C Logistics is headquartered in Kigali, Rwanda and powers
            logistics operations across the East African Community.
          </p>
          <div className="hp-coverage-grid">
            <div className="hp-coverage-card">
              <div className="hp-coverage-flag">🇷🇼</div>
              <h3>Rwanda</h3>
              <p>Kigali, Huye, Musanze, Rubavu, Rusizi</p>
            </div>
            <div className="hp-coverage-card">
              <div className="hp-coverage-flag">🇺🇬</div>
              <h3>Uganda</h3>
              <p>Kampala, Entebbe, Jinja, Mbarara</p>
            </div>
            <div className="hp-coverage-card">
              <div className="hp-coverage-flag">🇰🇪</div>
              <h3>Kenya</h3>
              <p>Nairobi, Mombasa, Kisumu, Nakuru</p>
            </div>
            <div className="hp-coverage-card">
              <div className="hp-coverage-flag">🇹🇿</div>
              <h3>Tanzania</h3>
              <p>Dar es Salaam, Arusha, Mwanza, Dodoma</p>
            </div>
            <div className="hp-coverage-card">
              <div className="hp-coverage-flag">🇧🇮</div>
              <h3>Burundi</h3>
              <p>Bujumbura, Gitega, Ngozi</p>
            </div>
            <div className="hp-coverage-card">
              <div className="hp-coverage-flag">🌍</div>
              <h3>Expanding</h3>
              <p>DRC, South Sudan, Ethiopia</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="hp-section hp-steps" id="how-it-works">
        <div className="hp-section-inner">
          <div className="hp-section-tag">How It Works</div>
          <h2 className="hp-section-title">
            From booking to delivery in 4 steps
          </h2>
          <p className="hp-section-sub">
            A clear, structured workflow that keeps every stakeholder informed
            at every stage.
          </p>
          <div className="hp-steps-grid">
            {steps.map((s) => (
              <div className="hp-step" key={s.n}>
                <div className="hp-step-num">{s.n}</div>
                <div className="hp-step-title">{s.title}</div>
                <div className="hp-step-desc">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="hp-section hp-faq">
        <div className="hp-section-inner">
          <div className="hp-section-tag">Frequently Asked Questions</div>
          <h2 className="hp-section-title">
            Everything you need to know about E.H.C Logistics
          </h2>
          <div className="hp-faq-list">
            <details className="hp-faq-item">
              <summary>What is E.H.C Logistics?</summary>
              <p>
                E.H.C Logistics is Rwanda's leading logistics management
                platform, offering real-time shipment tracking, multi-branch
                operations, inventory management, and payment processing for
                businesses across East Africa. We serve logistics companies,
                courier services, freight forwarders, and e-commerce businesses
                in Rwanda, Uganda, Kenya, Tanzania, and Burundi.
              </p>
            </details>
            <details className="hp-faq-item">
              <summary>Does E.H.C Logistics operate in Rwanda?</summary>
              <p>
                Yes. E.H.C Logistics is headquartered in Kigali, Rwanda and
                serves logistics companies across all provinces including Kigali
                City, Eastern Province, Western Province, Northern Province, and
                Southern Province. We support operations in major cities like
                Kigali, Huye, Musanze, Rubavu, and Rusizi.
              </p>
            </details>
            <details className="hp-faq-item">
              <summary>How do I track a shipment with E.H.C Logistics?</summary>
              <p>
                Enter your tracking number on our homepage or visit
                ehclogistics.rw/track. No account is required to track a
                shipment. You'll see real-time GPS location, status updates, and
                estimated delivery time for shipments across Rwanda and East
                Africa.
              </p>
            </details>
            <details className="hp-faq-item">
              <summary>
                Can I integrate E.H.C Logistics with my own system?
              </summary>
              <p>
                Yes. E.H.C Logistics provides a full REST API with API key
                authentication and HMAC-signed webhooks for seamless integration
                with your existing systems. Perfect for e-commerce platforms,
                ERP systems, and custom logistics solutions in Rwanda and East
                Africa.
              </p>
            </details>
            <details className="hp-faq-item">
              <summary>What makes E.H.C Logistics the best in Rwanda?</summary>
              <p>
                E.H.C Logistics is purpose-built for African logistics companies
                with features like multi-branch management, role-based access
                for 9 user types, proof of delivery with photo upload, hazardous
                materials support, inventory management, and payment tracking —
                all in one platform. We understand the unique challenges of
                logistics in Rwanda and East Africa.
              </p>
            </details>
            <details className="hp-faq-item">
              <summary>How much does E.H.C Logistics cost?</summary>
              <p>
                E.H.C Logistics offers flexible pricing plans starting with a
                free tier for small businesses. Contact us for enterprise
                pricing tailored to your logistics operation in Rwanda or East
                Africa. All plans include real-time tracking, multi-branch
                support, and API access.
              </p>
            </details>
            <details className="hp-faq-item">
              <summary>Is E.H.C Logistics available in Kinyarwanda?</summary>
              <p>
                Currently, E.H.C Logistics is available in English and French.
                We are working on adding Kinyarwanda and Swahili language
                support to better serve logistics companies across Rwanda and
                East Africa.
              </p>
            </details>
            <details className="hp-faq-item">
              <summary>
                Can I use E.H.C Logistics for cross-border shipments?
              </summary>
              <p>
                Yes. E.H.C Logistics supports cross-border logistics operations
                across the East African Community (EAC). Track shipments from
                Rwanda to Uganda, Kenya, Tanzania, Burundi, and beyond with full
                customs documentation support.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* ── PWA floating install button ── */}
      {canInstall && (
        <button
          onClick={handleInstallClick}
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#1d4ed8",
            color: "#fff",
            border: "none",
            borderRadius: 50,
            padding: "12px 20px",
            fontWeight: 700,
            fontSize: 14,
            cursor: "pointer",
            boxShadow: "0 4px 20px rgba(29,78,216,0.45)",
          }}
        >
          <span style={{ fontSize: 18 }}>💻</span> Install App
        </button>
      )}

      {/* ── CTA ── */}
      <section className="hp-cta">
        <h2 className="hp-cta-title">
          Ready to transform your logistics operations in Rwanda?
        </h2>
        <p className="hp-cta-sub">
          Join leading logistics companies across East Africa. Create your free
          account and start managing shipments, branches, and payments in
          minutes.
        </p>
        <div className="hp-cta-actions">
          <Link to="/register" className="hp-btn-primary">
            Create free account →
          </Link>
          <Link to="/login" className="hp-btn-ghost">
            Sign in to existing account
          </Link>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="hp-footer">
        <div className="hp-footer-inner">
          <div className="hp-footer-brand">
            <img src="/logo.png" alt="E.H.C Logistics Rwanda" />
            <p>
              Rwanda's #1 logistics management platform. Serving logistics
              companies across Kigali, Rwanda, and East Africa with real-time
              tracking, multi-branch operations, and payment management.
            </p>
            <p style={{ marginTop: 12, fontSize: 13, opacity: 0.7 }}>
              📍 Headquartered in Kigali, Rwanda
              <br />
              🌍 Serving Rwanda, Uganda, Kenya, Tanzania & Burundi
            </p>
          </div>
          <div className="hp-footer-col">
            <h4>Platform</h4>
            <a href="#features">Features</a>
            <a href="#how-it-works">How it works</a>
          </div>
          <div className="hp-footer-col">
            <h4>Account</h4>
            <Link to="/login">Sign in</Link>
            <Link to="/register">Register</Link>
            <Link to="/forgot-password">Forgot password</Link>
          </div>
          <div className="hp-footer-col">
            <h4>Tools</h4>
            <Link to="/track">Track Shipment</Link>
          </div>
        </div>
        <div className="hp-footer-bottom">
          <span>
            © {new Date().getFullYear()} E.H.C Logistics Platform. All rights
            reserved. | Logistics Software Rwanda | Courier Management Kigali
          </span>
          <span>🇷🇼 Proudly built in Africa for the World</span>
        </div>
      </footer>
    </div>
  );
};
